'use client';

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { CountrySelector } from '@kit/artists/components/country/country-selector';
import { NationalityPicker } from '@kit/artists/components/country/nationality-picker';
import type { CountryFieldRenderArgs } from '@kit/auditions/components';

/**
 * The `renderCountryField` slot every application surface passes to
 * `DynamicFormRenderer` / `StepperFormRenderer`: country-backed answers
 * (residence = single, nationalities = multi) render through the
 * `@kit/artists` pickers, which own the ISO dataset and flags.
 *
 * One definition, so the applicant (apply dialog, competition apply, edit
 * view) and the organizer (candidate sheet) see the identical control. Without
 * the slot a country field falls back to a plain dropdown of whatever options
 * the structure carries.
 *
 * `fieldProps` carry `FormControl`'s id / aria-describedby / aria-invalid, so
 * the field's `FormLabel` names the picker and `FormMessage` is announced.
 */
export function useRenderApplicationCountryField() {
  const { t } = useTranslation();

  return useCallback(
    ({ value, onChange, multiple, fieldProps }: CountryFieldRenderArgs) => {
      if (multiple) {
        return (
          <NationalityPicker
            {...fieldProps}
            value={Array.isArray(value) ? value : []}
            onChange={(codes) => onChange(codes)}
            placeholder={t('explore:competitionApply.addNationality')}
          />
        );
      }
      // CountrySelector names its search through `searchPlaceholder`; the
      // linked FormLabel names the field, so the field label is not needed.
      const { 'aria-label': _fieldLabel, ...controlProps } = fieldProps;
      return (
        <CountrySelector
          {...controlProps}
          value={typeof value === 'string' ? value : ''}
          onValueChange={(v) => onChange(v)}
          placeholder={t('explore:competitionApply.selectCountry')}
          searchPlaceholder={t('explore:competitionApply.searchCountries')}
        />
      );
    },
    [t],
  );
}
